/**
 * Approvals panel.
 *
 * Sits above the Chats panel in the left column. Lists every
 * determination in this tenant where the current user is the
 * invited approver (the email the operator typed into the "Send
 * for approval" sheet matches the user's account email). Rows
 * disappear when the determination transitions out of
 * ``pending_approval`` (approved or sent back for changes).
 *
 * Polls /v1/approvals/pending on the same cadence as the
 * documents tree so a sender's "Send for approval" click lands
 * in the approver's panel within a few seconds without a manual
 * refresh.
 */

import { useEffect, useState } from "react";
import {
  listPendingApprovals,
  loadConfig,
  type ApiError,
  type PendingApprovalRow,
} from "../api/client";

const POLL_INTERVAL_MS = 8000;

export function ApprovalsPanel({
  refreshKey,
  onSelectAssessment,
}: {
  refreshKey: number;
  onSelectAssessment?: (assessmentId: string) => void;
}) {
  const [rows, setRows] = useState<PendingApprovalRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const cfg = loadConfig();
    const refresh = () => {
      listPendingApprovals(cfg)
        .then((list) => {
          if (cancelled) return;
          setRows(list);
          setError(null);
        })
        .catch((err: ApiError) => {
          if (cancelled) return;
          setError(err.message ?? "Could not load approvals");
        });
    };
    setLoading(true);
    refresh();
    setLoading(false);
    const handle = window.setInterval(refresh, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, [refreshKey]);

  return (
    <div className="approvals-panel" aria-label="Approvals awaiting your action">
      <div className="col-head approvals-head">
        <div className="col-title">APPROVALS</div>
        {rows.length > 0 && (
          <span className="col-badge" aria-label={`${rows.length} pending`}>
            {rows.length}
          </span>
        )}
      </div>
      {loading && rows.length === 0 && (
        <div className="empty empty-compact">
          <p>Loading approvals…</p>
        </div>
      )}
      {error && (
        <div className="empty empty-compact" style={{ color: "var(--err)" }}>
          <p>{error}</p>
        </div>
      )}
      {!loading && !error && rows.length === 0 && (
        <div className="empty empty-compact">
          <p>Nothing waiting on you.</p>
        </div>
      )}
      <div className="approvals-list">
        {rows.map((r) => (
          <button
            key={r.assessment_id}
            type="button"
            className="approval-row"
            onClick={() => {
              if (onSelectAssessment) onSelectAssessment(r.assessment_id);
            }}
            title={
              r.contractor_name
                ? `${r.contractor_name} — ${r.contract_id} · ${r.variation_id}`
                : `${r.contract_id} · ${r.variation_id}`
            }
          >
            <span className="approval-row-title">
              {r.variation_id || r.assessment_id}
              {r.contractor_name && (
                <span className="approval-row-contractor">
                  {" · "}
                  {r.contractor_name}
                </span>
              )}
            </span>
            <span className="approval-row-meta">
              <span className="approval-row-people">
                <span className="approval-row-prepared">
                  {r.prepared_by
                    ? `Prepared by ${r.prepared_by}`
                    : "Preparer unknown"}
                </span>
                <span className="approval-row-approver">
                  {"Approver: "}
                  {r.approver_display_name || r.approver_email}
                </span>
              </span>
              {r.approval_sent_at && (
                <span className="approval-row-sent">
                  {new Date(r.approval_sent_at).toLocaleString("en-AU", {
                    dateStyle: "short",
                    timeStyle: "short",
                  })}
                </span>
              )}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
